/**
 * Plant Analyser — shared screen for DISCOM and Vendor roles.
 * FR-D11–FR-D18 (DISCOM) · FR-V06–FR-V09 (Vendor)
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronDown, ChevronRight, Search, X, Filter, Download,
  Zap, Activity, AlertTriangle, Ticket, Eye,
} from 'lucide-react';
import StatusPill from '../../components/shared/StatusPill';
import { vendorPlants } from '../../data/mockData';
import { PlantStatus, TicketStatus, VendorPlant } from '../../types';
import { relativeTime } from '../../utils/dateHelpers';
import { computeTileCounts, diagnose, tileFilterFn, TileKey } from '../../utils/plantDiagnosis';

// ── Types ────────────────────────────────────────────────────────────────────

type SortCol = 'id' | 'consumerName' | 'state' | 'prPercent' | 'todayKWh' | 'availabilityPercent' | 'activeAlarms' | 'lastSeen';
type SortDir = 'asc' | 'desc';

interface AdvFilters {
  circle: string; division: string; subDivision: string;  // DISCOM
  state: string; district: string; town: string; pincode: string; // Vendor
  capacityBand: string; inverterBrand: string; plantAge: string;
  prThreshold: string; availThreshold: string; alarmSeverity: string;
}

const ADV_DEFAULTS: AdvFilters = {
  circle: '', division: '', subDivision: '',
  state: '', district: '', town: '', pincode: '',
  capacityBand: '', inverterBrand: '', plantAge: '',
  prThreshold: '', availThreshold: '', alarmSeverity: '',
};

// ── Tile definitions ─────────────────────────────────────────────────────────

interface TileDef {
  key: TileKey;
  label: string;
  sub: string;
  textCls: string;
  bgCls: string;
  ringCls: string;
}

const TILES: TileDef[] = [
  // Row 1 — Performance
  { key: 'prBelow70',          label: 'PR below 70%',         sub: 'Current PR < 70%',                    textCls: 'text-red-600',    bgCls: 'bg-red-50',    ringCls: 'ring-red-500' },
  { key: 'prDropping7d',       label: 'PR dropping 7D+',      sub: 'Declined every day for 7+ days',       textCls: 'text-amber-600',  bgCls: 'bg-amber-50',  ringCls: 'ring-amber-500' },
  { key: 'peerUnderperformer', label: 'Peer underperformer',  sub: '>10% below pincode PR avg',            textCls: 'text-purple-600', bgCls: 'bg-purple-50', ringCls: 'ring-purple-500' },
  { key: 'lowCUF',             label: 'Low CUF',              sub: 'CUF < 15% (solar hours)',              textCls: 'text-blue-600',   bgCls: 'bg-blue-50',   ringCls: 'ring-blue-500' },
  { key: 'lowSpecificYield',   label: 'Low specific yield',   sub: '< 85 kWh/kWp this month',             textCls: 'text-teal-600',   bgCls: 'bg-teal-50',   ringCls: 'ring-teal-500' },
  // Row 2 — Availability + Connectivity
  { key: 'lowAvailability',    label: 'Low availability',     sub: '< 80% of solar hours',                textCls: 'text-green-600',  bgCls: 'bg-green-50',  ringCls: 'ring-green-500' },
  { key: 'notGenerating',      label: 'Not generating',       sub: 'Offline but telemetry present',        textCls: 'text-orange-600', bgCls: 'bg-orange-50', ringCls: 'ring-orange-500' },
  { key: 'activeAlarms',       label: 'Active alarms',        sub: '1+ unresolved alarms',                textCls: 'text-slate-700',  bgCls: 'bg-slate-100', ringCls: 'ring-slate-500' },
  { key: 'rmsOffline',         label: 'RMS offline',          sub: 'No heartbeat > 30 min',               textCls: 'text-pink-600',   bgCls: 'bg-pink-50',   ringCls: 'ring-pink-500' },
  { key: 'inverterNotRsp',     label: 'Inverter not responding', sub: 'Params null/stale > 30 min',       textCls: 'text-gray-500',   bgCls: 'bg-gray-100',  ringCls: 'ring-gray-400' },
];

const INVERTER_BRANDS = ['All', 'Sofar', 'Growatt', 'Solis', 'Delta', 'Sungrow'];
const CIRCLES  = ['', 'Ahmedabad', 'Gandhinagar', 'Ankleshwar', 'Kheda', 'Mehsana'];
const DIVISIONS: Record<string, string[]> = {
  Ahmedabad: ['Division East', 'Division West', 'Division Central'],
  Gandhinagar: ['Division North', 'Division South'],
  Ankleshwar: ['Division A', 'Division B'],
  Kheda: ['Division I', 'Division II'],
  Mehsana: ['Division M1', 'Division M2'],
};
const STATES_V   = ['', 'Gujarat', 'Tamil Nadu', 'Rajasthan', 'Maharashtra', 'Karnataka'];
const DISTRICTS_V = ['', 'Ahmedabad', 'Surat', 'Vadodara', 'Rajkot', 'Junagadh'];

// ── Helper: advanced filter active count ─────────────────────────────────────

function advCount(f: AdvFilters): number {
  return Object.values(f).filter(v => v !== '').length;
}

// ── Helper: apply advanced filters ───────────────────────────────────────────

function applyAdv(p: VendorPlant, f: AdvFilters, role: 'discom' | 'vendor'): boolean {
  if (role === 'discom') {
    if (f.circle      && p.circle      !== f.circle)      return false;
    if (f.division    && p.division    !== f.division)    return false;
    if (f.subDivision && p.subDivision !== f.subDivision) return false;
  } else {
    if (f.state    && p.state    !== f.state)    return false;
    if (f.district && p.district !== f.district) return false;
    if (f.town     && p.town     !== f.town)     return false;
    if (f.pincode  && p.pincode  !== f.pincode)  return false;
  }
  if (f.inverterBrand && f.inverterBrand !== 'All' && p.inverterBrand !== f.inverterBrand) return false;
  if (f.capacityBand) {
    const c = p.capacityKWp;
    if (f.capacityBand === '1-3'  && !(c >= 1  && c <= 3))  return false;
    if (f.capacityBand === '3-5'  && !(c > 3   && c <= 5))  return false;
    if (f.capacityBand === '5-10' && !(c > 5   && c <= 10)) return false;
    if (f.capacityBand === '>10'  && !(c > 10))             return false;
  }
  if (f.plantAge) {
    const a = p.plantAgeYears;
    if (f.plantAge === '<1'  && !(a < 1))              return false;
    if (f.plantAge === '1-3' && !(a >= 1 && a <= 3))   return false;
    if (f.plantAge === '>3'  && !(a > 3))              return false;
  }
  if (f.prThreshold) {
    if (f.prThreshold === '<65'   && !(p.prPercent < 65))               return false;
    if (f.prThreshold === '65-75' && !(p.prPercent >= 65 && p.prPercent < 75)) return false;
    if (f.prThreshold === '>75'   && !(p.prPercent >= 75))              return false;
  }
  if (f.availThreshold) {
    if (f.availThreshold === '<70'   && !(p.availabilityPercent < 70))                  return false;
    if (f.availThreshold === '70-80' && !(p.availabilityPercent >= 70 && p.availabilityPercent < 80)) return false;
    if (f.availThreshold === '>80'   && !(p.availabilityPercent >= 80))                 return false;
  }
  if (f.alarmSeverity) {
    if (f.alarmSeverity === 'critical' && !(p.activeAlarms >= 2)) return false;
    if (f.alarmSeverity === 'warning'  && !(p.activeAlarms >= 1)) return false;
  }
  return true;
}

// ── Main component ────────────────────────────────────────────────────────────

interface Props { role: 'discom' | 'vendor'; }

const PAGE_SIZE = 50;

export default function PlantAnalyserScreen({ role }: Props) {
  const navigate = useNavigate();
  const all = vendorPlants;

  // ── Tile counts — computed once from ALL plants, refreshed every 5 min ──
  const [counts, setCounts] = useState(() => computeTileCounts(all));
  useEffect(() => {
    const id = setInterval(() => setCounts(computeTileCounts(all)), 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [all]);

  // ── State ──────────────────────────────────────────────────────────────────
  const [activeTile, setActiveTile] = useState<TileKey | null>(null);
  const [rawSearch,  setRawSearch]  = useState('');
  const [search,     setSearch]     = useState('');
  const [showAdv,    setShowAdv]    = useState(false);
  const [adv,        setAdv]        = useState<AdvFilters>(ADV_DEFAULTS);
  const [pendingAdv, setPendingAdv] = useState<AdvFilters>(ADV_DEFAULTS);
  const [sortCol,    setSortCol]    = useState<SortCol>('id');
  const [sortDir,    setSortDir]    = useState<SortDir>('asc');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [page,       setPage]       = useState(1);

  // Debounce search 300 ms
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleSearchChange = useCallback((v: string) => {
    setRawSearch(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setSearch(v); setPage(1); }, 300);
  }, []);

  // ── Sort toggle ──────────────────────────────────────────────────────────
  const toggleSort = (col: SortCol) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
    setPage(1);
  };

  // ── Filtered & sorted list ───────────────────────────────────────────────
  const displayed = useMemo(() => {
    let list = all;
    if (activeTile) list = list.filter(tileFilterFn(activeTile));
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(p =>
        p.id.toLowerCase().includes(q) ||
        p.consumerName.toLowerCase().includes(q) ||
        p.consumerNumber.toLowerCase().includes(q) ||
        p.pincode.includes(q) ||
        p.mobile.includes(q) ||
        p.village.toLowerCase().includes(q) ||
        p.town.toLowerCase().includes(q) ||
        p.meterNumber.toLowerCase().includes(q)
      );
    }
    list = list.filter(p => applyAdv(p, adv, role));
    list = [...list].sort((a, b) => {
      let av: string | number = a[sortCol] as string | number;
      let bv: string | number = b[sortCol] as string | number;
      if (typeof av === 'string') av = av.toLowerCase();
      if (typeof bv === 'string') bv = bv.toLowerCase();
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [activeTile, search, adv, role, sortCol, sortDir, all]);

  const totalPages = Math.max(1, Math.ceil(displayed.length / PAGE_SIZE));
  const pageSlice  = displayed.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // ── Active filter chips (advanced) ───────────────────────────────────────
  const advChips = Object.entries(adv).filter(([, v]) => v !== '');

  // ── PR colour ────────────────────────────────────────────────────────────
  const prCls = (pr: number) =>
    pr >= 75 ? 'text-green-600 font-semibold' :
    pr >= 65 ? 'text-amber-600 font-semibold' :
               'text-red-600 font-semibold';

  // ── Sort header helper ───────────────────────────────────────────────────
  const SortTh = ({ col, label, right }: { col: SortCol; label: string; right?: boolean }) => (
    <th
      onClick={() => toggleSort(col)}
      className={`py-2.5 text-text-secondary font-semibold cursor-pointer select-none hover:text-accent transition-colors ${right ? 'text-right' : 'text-left'}`}
    >
      <span className="inline-flex items-center gap-0.5">
        {label}
        {sortCol === col && <span className="text-accent">{sortDir === 'asc' ? ' ↑' : ' ↓'}</span>}
      </span>
    </th>
  );

  const getStatusType = (s: PlantStatus): 'online' | 'offline' | 'warning' | 'inactive' => {
    if (s === PlantStatus.ONLINE)  return 'online';
    if (s === PlantStatus.OFFLINE) return 'offline';
    if (s === PlantStatus.WARNING || s === PlantStatus.FAULT) return 'warning';
    return 'inactive';
  };

  // ── Export CSV (filtered list) ───────────────────────────────────────────
  const exportCSV = () => {
    const header = 'Plant ID,Consumer,State,Inverter,Status,kWh Today,PR%,Alarms';
    const rows   = displayed.map(p =>
      `${p.id},${p.consumerName},${p.state},${p.inverterBrand},${p.status},${p.todayKWh},${p.prPercent},${p.activeAlarms}`
    );
    const blob = new Blob([[header, ...rows].join('\n')], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = 'plant-analyser.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 space-y-5">

      {/* ── Section header ── */}
      <div>
        <h1 className="text-base font-bold text-text-primary">Plant Analyser</h1>
        <p className="text-xs text-text-tertiary mt-0.5">{role === 'discom' ? 'PGVCL' : 'Zebron Solar'} · {all.length} plants · counts refresh every 5 min</p>
      </div>

      {/* ── SECTION 1: Triage tiles 2×5 ── */}
      <div className="grid grid-cols-5 gap-3">
        {TILES.map(t => {
          const count    = counts[t.key];
          const isActive = activeTile === t.key;
          return (
            <div
              key={t.key}
              onClick={() => { setActiveTile(prev => prev === t.key ? null : t.key); setPage(1); }}
              className={`rounded-card border p-4 cursor-pointer transition-all hover:shadow-md ${t.bgCls}
                ${isActive ? `ring-2 ${t.ringCls} border-transparent` : 'border-border shadow-card'}`}
            >
              <p className="text-xs text-text-secondary font-semibold leading-tight">{t.label}</p>
              <p className={`text-2xl font-bold mt-1 ${t.textCls}`}>{count}</p>
              <p className="text-xs text-text-tertiary mt-1">{t.sub}</p>
            </div>
          );
        })}
      </div>

      {/* ── SECTION 2: Filter bar ── */}
      <div className="space-y-2">
        <div className="bg-white border border-border rounded-card px-3 py-2 flex items-center gap-2 flex-wrap">
          {/* Date range */}
          <input type="date" defaultValue="2026-04-01"
            className="text-xs border border-border rounded-input px-2 py-1.5 focus:outline-none focus:border-accent" />
          <span className="text-text-tertiary text-xs">–</span>
          <input type="date" defaultValue="2026-04-28"
            className="text-xs border border-border rounded-input px-2 py-1.5 focus:outline-none focus:border-accent" />

          {/* Smart search */}
          <div className="relative flex-1 min-w-[220px]">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-tertiary" />
            <input
              type="text"
              placeholder="Search plant, consumer, pincode, mobile, village, meter…"
              value={rawSearch}
              onChange={e => handleSearchChange(e.target.value)}
              className="w-full text-xs border border-border rounded-input pl-7 pr-7 py-1.5 focus:outline-none focus:border-accent"
            />
            {rawSearch && (
              <button onClick={() => { setRawSearch(''); setSearch(''); setPage(1); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-primary">
                <X size={12} />
              </button>
            )}
          </div>

          {/* Filters button with badge */}
          <button
            onClick={() => setShowAdv(v => !v)}
            className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-input border transition-colors ${
              showAdv ? 'bg-accent text-white border-accent' : 'bg-white text-text-secondary border-border hover:border-accent hover:text-accent'
            }`}
          >
            <Filter size={12} />
            Filters
            {advCount(adv) > 0 && (
              <span className="bg-white text-accent rounded-full text-xs font-bold w-4 h-4 flex items-center justify-center leading-none">
                {advCount(adv)}
              </span>
            )}
          </button>

          <div className="flex-1" />

          {/* Active tile pill */}
          {activeTile && (
            <div className="flex items-center gap-1.5 text-xs bg-accent/10 text-accent px-2.5 py-1 rounded-pill border border-accent/30">
              {TILES.find(t => t.key === activeTile)?.label}
              <button onClick={() => setActiveTile(null)}><X size={10} /></button>
            </div>
          )}

          {/* Export CSV */}
          <button onClick={exportCSV}
            className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-input border border-border bg-white text-text-secondary hover:text-accent hover:border-accent transition-colors">
            <Download size={12} /> Export CSV
          </button>
        </div>

        {/* Advanced filter panel */}
        {showAdv && (
          <div className="bg-white border border-border rounded-card px-4 py-4 space-y-4">
            <div className="grid grid-cols-3 gap-6">
              {/* Location group */}
              <div className="space-y-2">
                <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider">Location</p>
                {role === 'discom' ? (
                  <>
                    <select className="w-full text-xs border border-border rounded-input px-2 py-1.5 focus:outline-none focus:border-accent"
                      value={pendingAdv.circle} onChange={e => setPendingAdv(p => ({ ...p, circle: e.target.value, division: '', subDivision: '' }))}>
                      <option value="">All Circles</option>
                      {CIRCLES.filter(Boolean).map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <select className="w-full text-xs border border-border rounded-input px-2 py-1.5 focus:outline-none focus:border-accent"
                      value={pendingAdv.division} disabled={!pendingAdv.circle}
                      onChange={e => setPendingAdv(p => ({ ...p, division: e.target.value, subDivision: '' }))}>
                      <option value="">All Divisions</option>
                      {(DIVISIONS[pendingAdv.circle] ?? []).map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                    <select className="w-full text-xs border border-border rounded-input px-2 py-1.5 focus:outline-none focus:border-accent"
                      value={pendingAdv.subDivision} disabled={!pendingAdv.division}
                      onChange={e => setPendingAdv(p => ({ ...p, subDivision: e.target.value }))}>
                      <option value="">All Sub-Divisions</option>
                      {['Sub-Div A', 'Sub-Div B', 'Sub-Div C'].map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </>
                ) : (
                  <>
                    <select className="w-full text-xs border border-border rounded-input px-2 py-1.5 focus:outline-none focus:border-accent"
                      value={pendingAdv.state} onChange={e => setPendingAdv(p => ({ ...p, state: e.target.value, district: '', town: '', pincode: '' }))}>
                      <option value="">All States</option>
                      {STATES_V.filter(Boolean).map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <select className="w-full text-xs border border-border rounded-input px-2 py-1.5 focus:outline-none focus:border-accent"
                      value={pendingAdv.district} disabled={!pendingAdv.state}
                      onChange={e => setPendingAdv(p => ({ ...p, district: e.target.value, town: '', pincode: '' }))}>
                      <option value="">All Districts</option>
                      {DISTRICTS_V.filter(Boolean).map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                    <select className="w-full text-xs border border-border rounded-input px-2 py-1.5 focus:outline-none focus:border-accent"
                      value={pendingAdv.town} disabled={!pendingAdv.district}
                      onChange={e => setPendingAdv(p => ({ ...p, town: e.target.value }))}>
                      <option value="">All Towns</option>
                      {['Bavla', 'Dholka', 'Sanand', 'Viramgam'].map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <input type="text" placeholder="Pincode" value={pendingAdv.pincode}
                      onChange={e => setPendingAdv(p => ({ ...p, pincode: e.target.value }))}
                      className="w-full text-xs border border-border rounded-input px-2 py-1.5 focus:outline-none focus:border-accent" />
                  </>
                )}
              </div>

              {/* Plant group */}
              <div className="space-y-2">
                <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider">Plant</p>
                <select className="w-full text-xs border border-border rounded-input px-2 py-1.5 focus:outline-none focus:border-accent"
                  value={pendingAdv.capacityBand} onChange={e => setPendingAdv(p => ({ ...p, capacityBand: e.target.value }))}>
                  <option value="">All Capacities</option>
                  <option value="1-3">1–3 kWp</option>
                  <option value="3-5">3–5 kWp</option>
                  <option value="5-10">5–10 kWp</option>
                  <option value=">10">&gt;10 kWp</option>
                </select>
                <select className="w-full text-xs border border-border rounded-input px-2 py-1.5 focus:outline-none focus:border-accent"
                  value={pendingAdv.inverterBrand} onChange={e => setPendingAdv(p => ({ ...p, inverterBrand: e.target.value }))}>
                  {INVERTER_BRANDS.map(b => <option key={b} value={b === 'All' ? '' : b}>{b}</option>)}
                </select>
                <select className="w-full text-xs border border-border rounded-input px-2 py-1.5 focus:outline-none focus:border-accent"
                  value={pendingAdv.plantAge} onChange={e => setPendingAdv(p => ({ ...p, plantAge: e.target.value }))}>
                  <option value="">All Ages</option>
                  <option value="<1">&lt; 1 year</option>
                  <option value="1-3">1–3 years</option>
                  <option value=">3">&gt; 3 years</option>
                </select>
              </div>

              {/* Performance group */}
              <div className="space-y-2">
                <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider">Performance</p>
                <select className="w-full text-xs border border-border rounded-input px-2 py-1.5 focus:outline-none focus:border-accent"
                  value={pendingAdv.prThreshold} onChange={e => setPendingAdv(p => ({ ...p, prThreshold: e.target.value }))}>
                  <option value="">Any PR</option>
                  <option value="<65">&lt; 65%</option>
                  <option value="65-75">65–75%</option>
                  <option value=">75">&gt; 75%</option>
                </select>
                <select className="w-full text-xs border border-border rounded-input px-2 py-1.5 focus:outline-none focus:border-accent"
                  value={pendingAdv.availThreshold} onChange={e => setPendingAdv(p => ({ ...p, availThreshold: e.target.value }))}>
                  <option value="">Any Availability</option>
                  <option value="<70">&lt; 70%</option>
                  <option value="70-80">70–80%</option>
                  <option value=">80">&gt; 80%</option>
                </select>
                <select className="w-full text-xs border border-border rounded-input px-2 py-1.5 focus:outline-none focus:border-accent"
                  value={pendingAdv.alarmSeverity} onChange={e => setPendingAdv(p => ({ ...p, alarmSeverity: e.target.value }))}>
                  <option value="">Any Alarms</option>
                  <option value="critical">Critical only (≥2)</option>
                  <option value="warning">Warning &amp; above (≥1)</option>
                </select>
              </div>
            </div>

            <div className="flex items-center gap-2 pt-1 border-t border-border">
              <button onClick={() => { setAdv(pendingAdv); setPage(1); setShowAdv(false); }}
                className="text-xs px-3 py-1.5 rounded-input bg-accent text-white hover:bg-accent/90 transition-colors">
                Apply filters
              </button>
              <button onClick={() => { setAdv(ADV_DEFAULTS); setPendingAdv(ADV_DEFAULTS); setPage(1); }}
                className="text-xs px-3 py-1.5 rounded-input border border-border text-text-secondary hover:text-text-primary transition-colors">
                Clear all
              </button>
            </div>
          </div>
        )}

        {/* Active filter chips */}
        {advChips.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap">
            {advChips.map(([k, v]) => (
              <span key={k} className="inline-flex items-center gap-1 text-xs bg-accent/10 text-accent border border-accent/30 px-2 py-0.5 rounded-pill">
                {k}: {v}
                <button onClick={() => { const next = { ...adv, [k]: '' }; setAdv(next); setPendingAdv(next); setPage(1); }}>
                  <X size={9} />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* ── SECTION 3: Plant table ── */}
      <div className="bg-white rounded-card border border-border shadow-card overflow-hidden">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <p className="text-xs font-semibold text-text-primary">
            {displayed.length} plant{displayed.length !== 1 ? 's' : ''}
            {activeTile ? ` · ${TILES.find(t => t.key === activeTile)?.label}` : ''}
          </p>
          <p className="text-xs text-text-tertiary">Page {page} of {totalPages}</p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-bg-secondary border-b border-border">
              <tr>
                <th className="w-7 py-2.5" />
                <SortTh col="id"          label="Plant ID" />
                <SortTh col="consumerName" label="Consumer" />
                <SortTh col="state"        label="State" />
                <th className="text-left py-2.5 text-text-secondary font-semibold">Inverter</th>
                <th className="text-center py-2.5 text-text-secondary font-semibold">Status</th>
                <SortTh col="todayKWh"          label="kWh Today"     right />
                <SortTh col="prPercent"          label="PR %"          right />
                <SortTh col="availabilityPercent" label="Avail %"      right />
                <SortTh col="lastSeen"            label="Last Data"    right />
                <SortTh col="activeAlarms"        label="Alarms"       right />
                <th className="text-right py-2.5 text-text-secondary font-semibold">Ticket</th>
              </tr>
            </thead>
            <tbody>
              {pageSlice.map(plant => {
                const isExp  = expandedId === plant.id;
                const dx     = diagnose(plant);
                const hasData = plant.rmsOfflineMinutes === null || plant.rmsOfflineMinutes <= 30;

                return (
                  <React.Fragment key={plant.id}>
                    <tr
                      onClick={() => setExpandedId(prev => prev === plant.id ? null : plant.id)}
                      className={`border-b border-border/50 cursor-pointer hover:bg-bg-secondary transition-colors
                        ${isExp ? 'bg-bg-tertiary' : ''}
                        ${plant.prPercent < 65 ? 'bg-red-50/30' : plant.prPercent < 70 ? 'bg-orange-50/30' : ''}`}
                    >
                      <td className="py-2.5 pl-3">
                        {isExp
                          ? <ChevronDown size={12} className="text-accent" />
                          : <ChevronRight size={12} className="text-text-tertiary" />}
                      </td>
                      {/* Plant ID — clickable link */}
                      <td className="py-2.5">
                        <button
                          onClick={e => { e.stopPropagation(); navigate('/consumer/dashboard'); }}
                          className="font-mono font-medium text-accent hover:underline"
                        >
                          {plant.id}
                        </button>
                      </td>
                      {/* Consumer with sub-line */}
                      <td className="py-2.5">
                        <p className="font-medium text-text-primary">{plant.consumerName}</p>
                        <p className="text-text-tertiary mt-0.5" style={{ fontSize: 10 }}>
                          {plant.consumerNumber} · {plant.pincode} · {plant.village}
                        </p>
                      </td>
                      <td className="py-2.5 text-text-secondary">{plant.state}</td>
                      <td className="py-2.5 text-text-secondary">{plant.inverterBrand}</td>
                      <td className="py-2.5 text-center"><StatusPill status={getStatusType(plant.status)} /></td>
                      <td className="py-2.5 text-right font-medium text-text-primary">{plant.todayKWh}</td>
                      <td className={`py-2.5 text-right ${hasData ? prCls(plant.prPercent) : 'text-text-tertiary'}`}>
                        {hasData ? `${plant.prPercent}%` : '—'}
                      </td>
                      <td className={`py-2.5 text-right ${plant.availabilityPercent < 80 ? 'text-amber-600' : 'text-text-secondary'}`}>
                        {plant.availabilityPercent}%
                      </td>
                      <td className="py-2.5 text-right text-text-tertiary">{relativeTime(plant.lastSeen)}</td>
                      <td className={`py-2.5 text-right ${plant.activeAlarms > 0 ? 'text-red-600 font-semibold' : 'text-text-secondary'}`}>
                        {plant.activeAlarms > 0 ? plant.activeAlarms : '—'}
                      </td>
                      <td className={`py-2.5 text-right ${plant.openTickets > 0 ? 'text-blue-600 font-semibold' : 'text-text-secondary'}`}>
                        {plant.openTickets > 0 ? plant.openTickets : '—'}
                      </td>
                    </tr>

                    {/* ── SECTION 4: Expanded row ── */}
                    {isExp && (
                      <tr className="border-b border-border/50">
                        <td colSpan={12} className="p-0">
                          <div className="bg-bg-secondary px-5 py-4 flex gap-6">

                            {/* Left — 5 KPI mini cards */}
                            <div className="grid grid-cols-5 gap-3 flex-1">
                              {[
                                {
                                  label: 'PR vs baseline',
                                  value: hasData ? `${plant.prPercent - plant.baselinePR90d > 0 ? '+' : ''}${(plant.prPercent - plant.baselinePR90d).toFixed(1)}%` : '—',
                                  sub: hasData
                                    ? (plant.prPercent >= plant.baselinePR90d - 1 ? '↑ Rising / stable' : '↓ Dropping')
                                    : 'No data',
                                  color: hasData && plant.prPercent < plant.baselinePR90d - 3 ? 'text-red-600' : 'text-success',
                                },
                                {
                                  label: 'Peer PR delta',
                                  value: hasData ? `${plant.prPercent - plant.peerPRAvg > 0 ? '+' : ''}${(plant.prPercent - plant.peerPRAvg).toFixed(1)}%` : '—',
                                  sub: 'vs pincode avg',
                                  color: hasData && plant.prPercent < plant.peerPRAvg - 5 ? 'text-red-600' : 'text-success',
                                },
                                {
                                  label: 'CUF',
                                  value: hasData ? `${plant.cufPercent}%` : '—',
                                  sub: 'of solar hours',
                                  color: plant.cufPercent < 15 ? 'text-amber-600' : 'text-text-primary',
                                },
                                {
                                  label: 'Specific yield',
                                  value: hasData ? `${plant.specificYieldMonthly}` : '—',
                                  sub: 'kWh/kWp · month',
                                  color: plant.specificYieldMonthly < 85 ? 'text-amber-600' : 'text-text-primary',
                                },
                                {
                                  label: 'Availability',
                                  value: hasData ? `${plant.availabilityPercent}%` : '—',
                                  sub: 'of solar hours',
                                  color: plant.availabilityPercent < 80 ? 'text-amber-600' : 'text-success',
                                },
                              ].map(card => (
                                <div key={card.label} className="bg-white rounded-input border border-border p-3">
                                  <p className="text-xs text-text-secondary font-semibold">{card.label}</p>
                                  <p className={`text-base font-bold mt-1 ${card.color}`}>{card.value}</p>
                                  <p className="text-xs text-text-tertiary mt-0.5">{card.sub}</p>
                                </div>
                              ))}
                            </div>

                            {/* Right — Diagnosis + actions */}
                            <div className="w-64 flex-shrink-0 flex flex-col gap-3">
                              {/* Root cause chip */}
                              <div>
                                <p className="text-xs text-text-secondary font-semibold mb-1">Root cause hint</p>
                                <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-pill border font-medium ${dx.chipColor} ${dx.chipBorder}`}>
                                  <AlertTriangle size={11} />
                                  {dx.hint}
                                </span>
                              </div>

                              {/* Last alarm — only if active alarms exist */}
                              {plant.lastAlarmText && plant.activeAlarms > 0 && (
                                <p className="text-xs text-text-secondary">
                                  <span className="font-semibold text-red-600">Last alarm: </span>
                                  {plant.lastAlarmText} · {relativeTime(plant.lastSeen)}
                                </p>
                              )}

                              {/* Action buttons */}
                              <div className="flex flex-col gap-1.5 mt-auto">
                                <button
                                  onClick={e => { e.stopPropagation(); }}
                                  className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-input bg-accent text-white hover:bg-accent/90 transition-colors"
                                >
                                  <Ticket size={11} /> Raise ticket
                                </button>
                                {plant.activeAlarms > 0 && (
                                  <button
                                    onClick={e => { e.stopPropagation(); }}
                                    className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-input border border-warning text-warning hover:bg-warning/10 transition-colors"
                                  >
                                    <Zap size={11} /> Acknowledge alarm
                                  </button>
                                )}
                                <button
                                  onClick={e => { e.stopPropagation(); navigate('/consumer/dashboard'); }}
                                  className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-input border border-border text-text-secondary hover:text-accent hover:border-accent transition-colors"
                                >
                                  <Eye size={11} /> View plant
                                </button>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}

              {pageSlice.length === 0 && (
                <tr>
                  <td colSpan={12} className="py-10 text-center text-xs text-text-tertiary">
                    No plants match the current filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-border flex items-center justify-between">
            <p className="text-xs text-text-tertiary">
              Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, displayed.length)} of {displayed.length}
            </p>
            <div className="flex items-center gap-1">
              <button disabled={page === 1} onClick={() => setPage(p => p - 1)}
                className="text-xs px-2.5 py-1 rounded-input border border-border disabled:opacity-40 hover:border-accent hover:text-accent transition-colors">
                ← Prev
              </button>
              {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => i + 1).map(n => (
                <button key={n} onClick={() => setPage(n)}
                  className={`text-xs w-7 h-7 rounded-input border transition-colors ${
                    page === n ? 'bg-accent text-white border-accent' : 'border-border hover:border-accent hover:text-accent'
                  }`}>{n}</button>
              ))}
              <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)}
                className="text-xs px-2.5 py-1 rounded-input border border-border disabled:opacity-40 hover:border-accent hover:text-accent transition-colors">
                Next →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
